import React, { useMemo, useState } from 'react';
import {
    Box,
    Text,
    Loader,
    Alert,
    Paper,
    Tabs,
    Card,
    Group,
    Stack,
    Anchor,
    Table,
    ScrollArea,
    ActionIcon,
    TextInput,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const ColorLegend = () => (
    <Card withBorder radius="md" style={{ width: '350px' }}>
        <Text size="sm" fw={500} mb="xs">
            Legend
        </Text>
        <Stack gap="xs">
            {[
                { color: '#73A57D', label: 'Pace 100% or more' },
                { color: '#D1A752', label: 'Pace 75%-100%' },
                { color: '#D88C8C', label: 'Pace less than 75%' },
            ].map(({ color, label }) => (
                <Group key={label} gap="xs" align="flex-start">
                    <Box
                        style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: color,
                            borderRadius: '2px',
                            flexShrink: 0,
                            marginTop: '2px',
                        }}
                    />
                    <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
                        {label}
                    </Text>
                </Group>
            ))}
        </Stack>
    </Card>
);

const SalesPerformanceScorecardBusinessGrowthPaceDetail = () => {
    const { ccode, mode } = useParams();
    const [activeTab, setActiveTab] = useState(mode);
    const navigate = useNavigate();

    const { data, isLoading, error } = useAuthenticatedData(`${config.apiBaseUrl}/sales/scorecard/${ccode}/newBusinessGrowthPace${mode.toUpperCase()}`);
    const { data: salesPersonName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

    // State for pop-ups and search
    const [openMembership, setOpenMembership] = useState(false);
    const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
    const [openDetailsQuery, setOpenDetailsQuery] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Clear handlers for proper dialog closing
    const handleCloseMembership = () => setOpenMembership(false);
    const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
    const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

    const { columns, rows, filteredRows, totals } = useMemo(() => {
        if (!data) return { columns: [], rows: [], filteredRows: [], totals: {} };

        const previousYear = (new Date().getFullYear() - 1).toString();
        const currentYear = (new Date().getFullYear()).toString();
        const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
        const previousQuarter = currentQuarter - 1 || 4;
        const previousQuarterYear = previousQuarter === 4 ? previousYear : currentYear;

        let paceHeaderName = `Pace for ${currentYear}`;
        let lastActualHeaderName = `${previousYear} Total`;
        let growthActualHeaderName = 'Year Over Year Growth Actual';
        let growthPaceHeaderName = 'Year Over Year Growth Pace';

        if (activeTab === 'qtd') {
            paceHeaderName = `Pace for Q${currentQuarter} ${currentYear}`;
            lastActualHeaderName = `Q${previousQuarter} ${previousQuarterYear} Total`;
            growthActualHeaderName = 'Quarter Over Quarter Growth Actual';
            growthPaceHeaderName = 'Quarter Over Quarter Growth Pace';
        } else if (activeTab === 'mtd') {
            const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });
            const lastMonthDate = new Date();
            lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
            const lastMonth = lastMonthDate.toLocaleString('en-US', { month: 'long' });
            paceHeaderName = `Pace for ${currentMonth}`;
            lastActualHeaderName = `${lastMonth} Total`;
            growthActualHeaderName = 'Month Over Month Growth Actual';
            growthPaceHeaderName = 'Month Over Month Growth Pace';
        }

        const columns = {
            paceHeaderName,
            lastActualHeaderName,
            growthActualHeaderName,
            growthPaceHeaderName
        };

        // Calculate sums first
        const currentActualSum = data.reduce((sum, account) => sum + (account.currentActual || 0), 0);
        const currentPaceSum = data.reduce((sum, account) => sum + (account.currentPace || 0), 0);
        const lastActualSum = data.reduce((sum, account) => sum + (account.lastActual || 0), 0);

        // Calculate totals
        const totals = {
            currentActual: currentActualSum,
            currentPace: currentPaceSum,
            lastActual: lastActualSum,
            growthActual: lastActualSum !== 0
                ? Number((currentActualSum / lastActualSum * 100).toFixed(2))
                : 0,
            growthPace: lastActualSum !== 0
                ? Number((currentPaceSum / lastActualSum * 100).toFixed(2))
                : 0,
        };

        // Create rows
        const regularRows = data.map(account => ({
            id: account.accountNumber,
            account: `${account.accountName} (${account.accountNumber})`,
            marketSegment: account.marketSegment || 'N/A',
            colorCategory: account.colorCategory,
            currentActual: account.currentActual || 0,
            currentPace: account.currentPace || 0,
            lastActual: account.lastActual || 0,
            growthActual: account.growthActual || 0,
            growthPace: account.growthPace || 0
        })).sort((a, b) => a.account.localeCompare(b.account));

        // Add totals row
        const rows = [
            ...regularRows,
            {
                id: 'TOTAL',
                account: 'TOTAL',
                colorCategory: 'total',
                marketSegment: '',
                currentActual: totals.currentActual,
                currentPace: totals.currentPace,
                lastActual: totals.lastActual,
                growthActual: totals.growthActual,
                growthPace: totals.growthPace
            }
        ];

        // Filter rows based on search term
        const filteredRows = rows.filter(row =>
            !searchTerm ||
            row.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.marketSegment.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return { columns, rows, filteredRows, totals };
    }, [data, mode, activeTab, searchTerm]);

    const handleTabChange = (value) => {
        navigate(`/sales/salesperson/${ccode}/scorecard/new-business-growth-pace/${value}`);
        setActiveTab(value);
    };

    const getRowColor = (colorCategory) => {
        switch (colorCategory?.toLowerCase()) {
            case 'red': return '#D88C8C';
            case 'green': return '#73A57D';
            case 'yellow': return '#D1A752';
            case 'total': return '#e0e0e0';
            default: return undefined;
        }
    };

    const formatCurrency = (value) => {
        if (!value || value === 0) return '';
        return value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '';
        return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)}%`;
    };

    const exportToCSV = () => {
        if (!filteredRows.length) return;

        const headers = ['Account', 'Market Segment', 'Grand Total', columns.paceHeaderName, columns.lastActualHeaderName, columns.growthActualHeaderName, columns.growthPaceHeaderName];
        const csvContent = [
            headers.join(','),
            ...filteredRows.map(row => [
                `"${row.account}"`,
                `"${row.marketSegment}"`,
                row.currentActual,
                row.currentPace,
                row.lastActual,
                row.growthActual,
                row.growthPace
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'growth-pace-details.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (isLoading || isLoadingName) {
        return (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Loader />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error">
                {error.message}
            </Alert>
        );
    }

    return (
        <Paper shadow="md" radius="md"  >
            <Stack gap="xs">
                {/* Header Section with Title and Helper Links */}
                <Box>
                    <Text
                        component="h1"
                        style={{
                            fontFamily: 'Orbitron, sans-serif',
                            color: '#FDB813',
                            fontStyle: 'italic',
                            fontSize: '1.5rem',
                            marginBottom: 0,
                            textAlign: 'left'
                        }}
                    >
                        Growth Pace Detail - {salesPersonName}
                    </Text>

                    {/* Technical options: small links */}
                    <Group gap="xs" mb="sm">

                        <Anchor
                            size="xs"
                            c="dimmed"
                            onClick={() =>
                                window.open(
                                    '/query-commander/editor?queryKey=sales-individual-month-over-month-growth-pace',
                                    '_blank'
                                )
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Summary Query
                        </Anchor>
                        <Anchor
                            size="xs"
                            c="dimmed"
                            onClick={() =>
                                window.open(
                                    '/query-commander/editor?queryKey=sales-individual-new-business-growth-pace-mtd-details',
                                    '_blank'
                                )
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            Details Query
                        </Anchor>
                    </Group>
                </Box>

                {/* Info Cards - tighter spacing */}
                <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
                    <Box style={{ flex: 1, minWidth: '300px' }}>
                        <Card withBorder radius="md" style={{ height: '100%' }}>
                            <Text size="sm" fw={500} mb="xs">
                                About the Data
                            </Text>
                            <Text size="xs" c="dimmed">
                                This shows all sales to the accounts assigned to {salesPersonName}, regardless of who made the sale.
                                Currently you're seeing all sales {mode.toUpperCase()} and how they compare to the last period.
                                The Grand Total is the current actual sales for the selected period. Using business days and correctly accounting for holidays, that is projected
                                forward to determine the pace for the period.
                            </Text>
                        </Card>
                    </Box>
                    <ColorLegend />
                </Group>

                {/* Tabs - compact */}
                <Tabs
                    value={mode}
                    onChange={handleTabChange}
                    styles={(theme) => ({
                        tab: {
                            color: theme.colorScheme === 'dark' ? '#b3b3b3' : '#666666',
                            '&[data-active]': {
                                color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
                                fontWeight: 'bold',
                            },
                            padding: '6px 12px',
                        },
                    })}
                >
                    <Tabs.List>
                        <Tabs.Tab value="mtd">Month-to-Date</Tabs.Tab>
                        <Tabs.Tab value="qtd">Quarter-to-Date</Tabs.Tab>
                        <Tabs.Tab value="ytd">Year-to-Date</Tabs.Tab>
                    </Tabs.List>
                </Tabs>

                {/* Toolbar - reduced spacing */}
                <Group justify="space-between" mb="xs">
                    <TextInput
                        placeholder="Search accounts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        leftSection={<IconSearch size={16} />}
                        style={{ width: '300px' }}
                        size="sm"
                    />
                    <Group gap="xs">
                        <ActionIcon
                            variant="default"
                            onClick={exportToCSV}
                            title="Export to CSV"
                            size="sm"
                        >
                            <IconDownload size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="default"
                            onClick={() => window.print()}
                            title="Print"
                            size="sm"
                        >
                            <IconPrinter size={16} />
                        </ActionIcon>
                    </Group>
                </Group>

                {/* Data Table - compact height */}
                <Paper withBorder style={{ height: '500px' }}>
                    <ScrollArea style={{ height: '100%' }}>
                        <Table striped highlightOnHover size="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: '250px' }}>Account</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>Market Segment</Table.Th>
                                    <Table.Th style={{ width: '130px' }}>Grand Total</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>{columns.paceHeaderName}</Table.Th>
                                    <Table.Th style={{ width: '150px' }}>{columns.lastActualHeaderName}</Table.Th>
                                    <Table.Th style={{ width: '180px' }}>{columns.growthActualHeaderName}</Table.Th>
                                    <Table.Th style={{ width: '180px' }}>{columns.growthPaceHeaderName}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {filteredRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{
                                            backgroundColor: getRowColor(row.colorCategory),
                                            fontWeight: row.id === 'TOTAL' ? 'bold' : 'normal',
                                        }}
                                    >
                                        <Table.Td>
                                            <Text size="xs" fw={row.id === 'TOTAL' ? 700 : 500} c="dark">
                                                {row.account}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" c="dark">
                                                {row.marketSegment}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" fw={600} c="dark">
                                                {formatCurrency(row.currentActual)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" fw={600} c="dark">
                                                {formatCurrency(row.currentPace)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" fw={600} c="dark">
                                                {formatCurrency(row.lastActual)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" fw={600} c="dark">
                                                {formatPercent(row.growthActual)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="xs" fw={600} c="dark">
                                                {formatPercent(row.growthPace)}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Paper>

                {/* Results count - minimal spacing */}
                <Text size="xs" c="dimmed">
                    Showing {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
                </Text>
            </Stack>

            {/* Pop-up dialogs */}
            <MembershipPopup open={openMembership} onClose={handleCloseMembership} />

            <SQLEditorPopup
                open={openSummaryQuery}
                onClose={handleCloseSummaryQuery}
                title="Summary Query"
                endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/growth-pace-summary`}
            />

            <SQLEditorPopup
                open={openDetailsQuery}
                onClose={handleCloseDetailsQuery}
                title="Details Query"
                endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/growth-pace-details`}
            />
        </Paper>
    );
};

export default SalesPerformanceScorecardBusinessGrowthPaceDetail;